import { letterCount, textRuns } from '../analysis/format';
import type { Role } from '../analysis/structure';
import type { Paragraph } from '../model/document';
import { GOST_2_105, GOST_7_32 } from '../profiles/ids';
import { countOf, CYRILLIC_TO_LATIN, hasCyrillic, LATIN_TO_CYRILLIC, NBSP, nbspSpots, truncate } from '../util/text';
import { cmToTwips } from '../util/units';
import { defineRule, type FixAction, type Rule, type RuleContext } from './types';
import { PARAS, quote } from './util';

const TEXT_ROLES = new Set<Role>(['body', 'list', 'note', 'formula-where', 'bib', 'heading', 'appendix-title', 'table-cell', 'caption']);

/** Абзацы основного текста (без титула, содержания и программного кода). */
const flowParagraphs = (ctx: RuleContext, roles: ReadonlySet<Role> = TEXT_ROLES): Paragraph[] =>
  ctx.model.paragraphs.filter((p) => !p.textBox && roles.has(ctx.s.roles[p.index]!));

// ─── Имена в свойствах файла ────────────────────────────────────────────

/** Слова, из которых состоят безличные имена учётных записей. */
const GENERIC_NAME_WORDS = new Set([
  'user', 'users', 'admin', 'administrator', 'root', 'guest', 'owner', 'home', 'microsoft', 'office', 'windows', 'word',
  'author', 'unknown', 'student', 'пользователь', 'администратор', 'гость', 'владелец', 'компьютер', 'автор', 'студент',
  'учетная', 'запись', 'server', 'desktop', 'laptop', 'notebook', 'computer', 'lenovo', 'asus', 'acer', 'dell', 'huawei', 'honor', 'xiaomi', 'redmi', 'apple', 'macbook', 'samsung',
]);

const TRANSLIT: Readonly<Record<string, string>> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e',
  ю: 'yu', я: 'ya',
};

const nameWords = (s: string): string[] => s.toLowerCase().replace(/ё/g, 'е').match(/\p{L}+/gu) ?? [];

/** Ключ для сравнения имён в разных алфавитах: «Александр», Aleksandr и Alexander сходятся. */
const latinKey = (w: string): string =>
  [...w]
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('')
    .replace(/x/g, 'ks')
    .replace(/kh/g, 'h')
    .replace(/[jy]/g, 'i');

/** Одно и то же имя с точностью до окончания: «Иванов» и «Иванова». */
function sameName(a: string, b: string): boolean {
  const n = Math.min(a.length, b.length, 5);
  return n >= 3 && a.slice(0, n) === b.slice(0, n);
}

/** Имя человека, которого нет среди слов титульного листа. Безличные «User», «Admin» не считаются. */
function isForeignName(name: string, ownKeys: readonly string[]): boolean {
  // «XXX» и подобные заглушки — не имя.
  const words = nameWords(name).filter((w) => w.length >= 3 && !GENERIC_NAME_WORDS.has(w) && !/^(.)\1+$/u.test(w));
  if (!words.length) return false;
  return !words.some((w) => {
    const k = latinKey(w);
    return ownKeys.some((d) => sameName(k, d));
  });
}

export const hygieneRules: Rule[] = [
  defineRule({
    id: 'hygiene.homoglyphs',
    category: 'hygiene',
    title: 'Латиница в русских словах',
    severity: 'error',
    why: 'Латинские «а», «о», «с», «р» внутри русских слов появляются при копировании из PDF. «Антиплагиат» помечает такие документы как подозрительные — это может стоить работе допуска.',
    check(ctx) {
      const paras = [...ctx.model.paragraphs, ...ctx.model.textBoxes].filter((p) => ctx.s.roles[p.index] !== 'toc' || p.textBox);
      for (const p of paras) {
        if (!hasCyrillic(p.text)) continue;
        const code = !p.textBox && ctx.s.roles[p.index] === 'code';
        const marks: Array<[number, number]> = [];
        const fix: FixAction[] = [];
        const samples: string[] = [];
        const words = [...p.text.matchAll(/\p{L}+/gu)];
        words.forEach((m, wi) => {
          const word = m[0];
          const cyr = (word.match(/[Ѐ-ӿ]/g) ?? []).length;
          const lat = (word.match(/[A-Za-z]/g) ?? []).length;
          let foreign: Readonly<Record<string, string>> | undefined;
          if (cyr && lat) foreign = cyr >= lat ? LATIN_TO_CYRILLIC : CYRILLIC_TO_LATIN;
          else if (!code && (word === 'c' || word === 'o')) {
            // Латинская «c» или «o» перед русским словом — это предлог «с» или «о» не в той раскладке.
            // Другие одиночные буквы (K, T, a, y) обычно переменные в формулах — их не трогаем,
            // а в программном коде одиночная буква почти всегда имя переменной.
            const prev = words[wi - 1]?.[0];
            const next = words[wi + 1]?.[0];
            if (prev && next && hasCyrillic(prev) && /^[а-яё]/.test(next)) foreign = LATIN_TO_CYRILLIC;
          }
          if (!foreign) return;
          let found = false;
          for (let k = 0; k < word.length; k++) {
            const ch = word[k]!;
            const repl = foreign[ch];
            if (!repl) continue;
            const at = m.index + k;
            marks.push([at, at + 1]);
            fix.push({ kind: 'text', paragraph: p.index, start: at, end: at + 1, expect: ch, replace: repl });
            found = true;
          }
          if (found && samples.length < 3) samples.push(`«${word}»`);
        });
        if (!marks.length) continue;
        ctx.report({
          paragraph: p.index,
          marks,
          message: `Смешаны алфавиты в ${samples.length > 1 ? 'словах' : 'слове'} ${samples.join(', ')}: ${countOf(marks.length, ['буква выглядит', 'буквы выглядят', 'букв выглядят'])} как русские, но набраны другой раскладкой`,
          fix,
          fixLabel: 'Заменить буквы',
        });
      }
    },
  }),

  defineRule({
    id: 'hygiene.hidden-text',
    category: 'hygiene',
    title: 'Скрытый и невидимый текст',
    severity: 'error',
    why: 'Белый, скрытый или микроскопический текст — известный способ обмануть проверку на заимствования. «Антиплагиат» его находит и помечает работу как подозрительную.',
    check(ctx) {
      for (const p of [...ctx.model.paragraphs, ...ctx.model.textBoxes]) {
        const kinds = new Set<string>();
        let sample = '';
        for (const r of textRuns(p)) {
          const t = p.text.slice(r.start, r.end);
          if (letterCount(t) < 2) continue;
          const shade = r.props.shading;
          const white = r.props.color === 'FFFFFF' && (!shade || shade === 'auto' || shade === 'FFFFFF');
          const hit = r.props.vanish ? 'скрытый' : white ? 'белый' : (r.props.sz ?? 20) <= 4 ? 'микроскопический' : (r.props.w ?? 100) <= 20 ? 'сжатый до нечитаемости' : '';
          if (!hit) continue;
          kinds.add(hit);
          sample ||= t;
        }
        if (!kinds.size) continue;
        ctx.report({ paragraph: p.index, message: `Невидимый текст (${[...kinds].join(', ')}): ${quote(sample, 50)}` });
      }
    },
  }),

  defineRule({
    id: 'hygiene.invisible-chars',
    category: 'hygiene',
    title: 'Невидимые символы',
    severity: 'warning',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.4', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.6.2' },
    why: 'Символы нулевой ширины ломают поиск и проверку на заимствования. Мягкие переносы в заголовках запрещены: переносы слов в заголовках не допускаются.',
    check(ctx) {
      for (const p of [...ctx.model.paragraphs, ...ctx.model.textBoxes]) {
        const heading = ctx.s.roles[p.index] === 'heading';
        const re = heading ? /[\u200b-\u200d\u2060\ufeff\u00ad]/g : /[\u200b-\u200d\u2060\ufeff]/g;
        const marks: Array<[number, number]> = [];
        const fix: FixAction[] = [];
        for (const m of p.text.matchAll(re)) {
          marks.push([m.index, m.index + 1]);
          fix.push({ kind: 'text', paragraph: p.index, start: m.index, end: m.index + 1, expect: m[0], replace: '' });
        }
        if (!marks.length) continue;
        ctx.report({
          paragraph: p.index,
          marks,
          message: heading && /\u00ad/.test(p.text) ? 'Мягкие переносы в заголовке' : marks.length === 1
              ? 'Невидимый символ нулевой ширины'
              : `${countOf(marks.length, ['невидимый символ', 'невидимых символа', 'невидимых символов'])} нулевой ширины`,
          fix,
          fixLabel: 'Удалить символы',
        });
      }
    },
  }),

  defineRule({
    id: 'hygiene.revisions',
    category: 'hygiene',
    title: 'Непринятые исправления и примечания',
    severity: 'warning',
    why: 'Документ сдают без режима рецензирования: чужие правки и заметки на полях видны проверяющему.',
    check(ctx) {
      const { revisions, comments, settings } = ctx.model;
      if (revisions > 0) {
        ctx.report({ message: `В документе ${countOf(revisions, ['непринятое исправление', 'непринятых исправления', 'непринятых исправлений'])} — примите или отклоните их: «Рецензирование → Принять все»` });
      }
      if (comments > 0) {
        ctx.report({ message: `В документе ${countOf(comments, ['примечание', 'примечания', 'примечаний'])} на полях — удалите их перед сдачей` });
      }
      if (settings.trackRevisions) {
        ctx.report({ severity: 'info', message: 'Включён режим исправлений: каждая правка будет видна проверяющему' });
      }
    },
  }),

  defineRule({
    id: 'hygiene.leading-spaces',
    category: 'hygiene',
    title: 'Отступы пробелами',
    severity: 'warning',
    aggregate: PARAS,
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 5.1.4' },
    why: 'Абзацный отступ задают в свойствах абзаца, а не пробелами или табуляцией: иначе он разный по всему тексту и съезжает при смене шрифта.',
    check(ctx) {
      const indent = cmToTwips(ctx.profile.text.firstLineIndentCm);
      for (const p of flowParagraphs(ctx, new Set<Role>(['body', 'list', 'note', 'bib']))) {
        const m = /^([ \u00a0]{2,}|[ \u00a0]*\t+[ \u00a0]*)/.exec(p.text);
        if (!m) continue;
        const fix: FixAction[] = [{ kind: 'text', paragraph: p.index, start: 0, end: m[0].length, expect: m[0], replace: '' }];
        if (ctx.s.roles[p.index] === 'body') fix.push({ kind: 'para', paragraph: p.index, set: { indFirst: indent, indLeft: 0 } });
        ctx.report({
          paragraph: p.index,
          marks: [[0, m[0].length]],
          message: m[0].includes('\t') ? 'Абзацный отступ сделан табуляцией' : 'Абзацный отступ сделан пробелами',
          fix,
          fixLabel: 'Заменить на абзацный отступ',
        });
      }
    },
  }),

  defineRule({
    id: 'hygiene.double-spaces',
    category: 'hygiene',
    title: 'Двойные пробелы',
    severity: 'info',
    why: 'Лишние пробелы растягивают строки при выравнивании по ширине и выдают небрежность. В программном коде пробелы не проверяются.',
    check(ctx) {
      for (const p of flowParagraphs(ctx)) {
        const marks: Array<[number, number]> = [];
        const fix: FixAction[] = [];
        for (const m of p.text.matchAll(/(?<=\S) {2,}(?=\S)/g)) {
          marks.push([m.index, m.index + m[0].length]);
          fix.push({ kind: 'text', paragraph: p.index, start: m.index, end: m.index + m[0].length, expect: m[0], replace: ' ' });
        }
        if (!marks.length) continue;
        ctx.report({ paragraph: p.index, marks, message: `Двойные пробелы: ${marks.length}`, fix, fixLabel: 'Убрать лишние пробелы' });
      }
    },
  }),

  defineRule({
    id: 'hygiene.dash',
    category: 'hygiene',
    title: 'Дефис вместо тире',
    severity: 'warning',
    why: 'Между словами ставят тире «—», а не дефис «-». В библиографических записях области тоже разделяют тире: «. — М., 2020. — 351 с.»',
    check(ctx) {
      const roles = new Set<Role>(['body', 'list', 'note', 'formula-where', 'bib', 'heading', 'table-cell']);
      for (const p of flowParagraphs(ctx, roles)) {
        const marks: Array<[number, number]> = [];
        const fix: FixAction[] = [];
        for (const m of p.text.matchAll(/(?<=[\p{L}\d.,;:)»"]) +(--?|−) +(?=[\p{L}\d(«"])/gu)) {
          const before = p.text[m.index - 1] ?? '';
          const after = p.text[m.index + m[0].length] ?? '';
          if (/\d/.test(before) && /\d/.test(after)) continue; // «5 - 3» — вычитание
          if (/[A-Za-z]/.test(before) && /[A-Za-z]/.test(after) && !hasCyrillic(p.text.slice(Math.max(0, m.index - 20), m.index + 20))) continue;
          const dashStart = m.index + m[0].indexOf(m[1]!);
          marks.push([dashStart, dashStart + m[1]!.length]);
          fix.push({ kind: 'text', paragraph: p.index, start: dashStart, end: dashStart + m[1]!.length, expect: m[1]!, replace: '—' });
        }
        if (!marks.length) continue;
        ctx.report({ paragraph: p.index, marks, message: `Дефис вместо тире: ${marks.length}`, fix, fixLabel: 'Заменить на тире' });
      }
    },
  }),

  defineRule({
    id: 'hygiene.quotes',
    category: 'hygiene',
    title: 'Кавычки-«ёлочки»',
    severity: 'info',
    why: 'В русском тексте используют кавычки «ёлочки»; прямые кавычки "..." выглядят как набранные на машинке. В программном коде кавычки не проверяются.',
    check(ctx) {
      for (const p of flowParagraphs(ctx)) {
        if (!p.text.includes('"') || !hasCyrillic(p.text)) continue;
        const positions = [...p.text.matchAll(/"/g)].map((m) => m.index);
        if (positions.length % 2) continue;
        const fix: FixAction[] = positions.map((at) => {
          const prev = p.text[at - 1];
          const opening = at === 0 || /[\s(\[«„—–-]/.test(prev ?? ' ');
          return { kind: 'text', paragraph: p.index, start: at, end: at + 1, expect: '"', replace: opening ? '«' : '»' };
        });
        ctx.report({
          paragraph: p.index,
          marks: positions.map((at) => [at, at + 1] as [number, number]),
          message: 'Прямые кавычки вместо «ёлочек»',
          fix,
          fixLabel: 'Заменить на «ёлочки»',
        });
      }
    },
  }),

  defineRule({
    id: 'hygiene.punctuation',
    category: 'hygiene',
    title: 'Пробелы у знаков препинания',
    severity: 'info',
    why: 'Перед запятой, точкой, двоеточием пробел не ставят, после них — ставят.',
    check(ctx) {
      for (const p of flowParagraphs(ctx, new Set<Role>(['body', 'list', 'note']))) {
        const marks: Array<[number, number]> = [];
        const fix: FixAction[] = [];
        for (const m of p.text.matchAll(/(?<=[\p{L}\d»)]) +(?=[,.;:!?](?:\s|$))/gu)) {
          marks.push([m.index, m.index + m[0].length]);
          fix.push({ kind: 'text', paragraph: p.index, start: m.index, end: m.index + m[0].length, expect: m[0], replace: '' });
        }
        for (const m of p.text.matchAll(/[,;](?=[а-яё])|[.](?=[А-ЯЁ][а-яё])/g)) {
          const at = m.index + 1;
          marks.push([m.index, at]);
          fix.push({ kind: 'text', paragraph: p.index, start: at, end: at, expect: '', replace: ' ' });
        }
        if (!marks.length) continue;
        ctx.report({ paragraph: p.index, marks, message: `Пробелы у знаков препинания: ${marks.length}`, fix, fixLabel: 'Расставить пробелы' });
      }
    },
  }),

  defineRule({
    id: 'hygiene.nbsp',
    category: 'hygiene',
    title: 'Неразрывные пробелы',
    severity: 'info',
    aggregate: PARAS,
    why: 'Число и единицу измерения, инициалы и фамилию, «№» и номер, «с.» и номер страницы разделяют неразрывным пробелом (Ctrl+Shift+Пробел), чтобы при переносе они не оказались на разных строках.',
    check(ctx) {
      for (const p of flowParagraphs(ctx, new Set<Role>(['body', 'list', 'note', 'caption', 'bib']))) {
        const at = nbspSpots(p.text);
        if (!at.length) continue;
        ctx.report({
          paragraph: p.index,
          marks: at.map((i) => [i, i + 1] as [number, number]),
          message: 'Обычный пробел там, где нужен неразрывный: «10 мм», «А. А. Смирнов», «с. 15»',
          fix: at.map((i): FixAction => ({ kind: 'text', paragraph: p.index, start: i, end: i + 1, expect: ' ', replace: NBSP, silent: true })),
          fixLabel: 'Поставить неразрывные пробелы',
        });
      }
    },
  }),

  defineRule({
    id: 'hygiene.empty-lines',
    category: 'hygiene',
    title: 'Пустые строки',
    severity: 'info',
    why: 'Отступы между абзацами задают интервалами, а новую страницу — разрывом (Ctrl+Enter). Пустые строки съезжают при любой правке текста. Пустые строки внутри листингов кода не считаются.',
    check(ctx) {
      const paras = ctx.model.paragraphs;
      const blank = (i: number): boolean => paras[i]!.isEmpty && !paras[i]!.cell && ctx.s.roles[i] !== 'code';
      let i = 0;
      while (i < paras.length) {
        const region = ctx.s.regions[i];
        if (!blank(i) || region === 'front' || region === 'toc') {
          i++;
          continue;
        }
        let j = i;
        while (j < paras.length && blank(j)) j++;
        const run = j - i;
        if (run >= 2) {
          ctx.report({ paragraph: i, message: `${countOf(run, ['пустая строка', 'пустые строки', 'пустых строк'])} подряд` });
        }
        i = j;
      }
    },
  }),

  defineRule({
    id: 'hygiene.line-breaks',
    category: 'hygiene',
    title: 'Разрывы строк внутри абзаца',
    severity: 'warning',
    aggregate: PARAS,
    why: 'Shift+Enter обрывает строку, не начиная абзац. При выравнивании по ширине строка перед разрывом растягивается на всю ширину — такие «дыры» сразу видны.',
    check(ctx) {
      for (const p of flowParagraphs(ctx, new Set<Role>(['body', 'note', 'bib']))) {
        if (!p.lineBreaks) continue;
        const justified = p.props.jc === 'justify';
        const marks = [...p.text.matchAll(/\n/g)].map((m) => [m.index, m.index + 1] as [number, number]);
        ctx.report({
          severity: justified ? undefined : 'info',
          paragraph: p.index,
          marks,
          message: justified
            ? 'Разрыв строки (Shift+Enter) внутри абзаца: строка перед ним растянута по ширине'
            : 'Разрыв строки (Shift+Enter) внутри абзаца: если это новый абзац, используйте Enter',
        });
      }
    },
  }),

  defineRule({
    id: 'hygiene.metadata',
    category: 'hygiene',
    title: 'Свойства файла',
    severity: 'info',
    why: 'Автор файла хранится в его свойствах («Файл → Сведения»), и проверяющий может их открыть. Если работа сделана на основе чужого файла, там останется чужое имя. Безличные имена вроде «User» и имена с титульного листа не считаются.',
    check(ctx) {
      const { creator, lastModifiedBy } = ctx.model.meta;
      if (!creator && !lastModifiedBy) return;
      const own = [...ctx.model.paragraphs.slice(0, Math.max(ctx.s.frontEnd, 60)), ...ctx.model.textBoxes];
      const ownKeys = [...new Set(own.flatMap((p) => nameWords(p.text)).filter((w) => w.length >= 3))].map(latinKey);
      const parts: string[] = [];
      if (creator && isForeignName(creator, ownKeys)) parts.push(`автор «${truncate(creator, 40)}»`);
      if (lastModifiedBy && lastModifiedBy !== creator && isForeignName(lastModifiedBy, ownKeys)) {
        parts.push(`последним сохранял «${truncate(lastModifiedBy, 40)}»`);
      }
      if (!parts.length) return;
      ctx.report({
        message: ctx.s.hasTitlePage
          ? `В свойствах файла ${parts.join(', ')} — ${parts.length > 1 ? 'этих имён' : 'этого имени'} нет на титульном листе. Если файл достался от другого человека, очистите свойства`
          : `В свойствах файла ${parts.join(', ')}. Если это не вы, очистите свойства`,
        fix: [{ kind: 'meta', clear: ['creator', 'lastModifiedBy'] }],
        fixLabel: 'Очистить автора',
      });
    },
  }),
];
